import { call, put, takeLatest, select } from 'redux-saga/effects';
import qs from 'query-string-for-all';

import { captureException } from '../../../client/sentry';
import { graphql } from '../../../globals/api/requests';
import { promptToast } from '../../../components/toast-container/actions';
import { isValidEmail } from '../../account/helpers';

import genericUserReviewFragment from '../generic-user-review.graphql';

import {
  productReviewVoted,
  storeReviewVoted,
  storeReviewCreated,
  storeRated,
  storeReviewDeleted,
  storeReviewEdited,
  ShopReviewType,
  storeReviewPublished,
} from '../tracking';

import {
  CREATE_REVIEW,
  CREATE_SHOP_REVIEW,
  EDIT_SHOP_REVIEW,
  DELETE_REVIEW,
  DELETE_SHOP_REVIEW,
  EDIT_REVIEW,
  REPORT_REVIEW,
  LIKE_REVIEW_TOGGLE,
  closeReviewDialog,
  reviewRequestError,
  reportReviewSuccess,
  reportReviewFailed,
  likeReviewSuccess,
  likeReviewFailed,
  createReviewSuccess,
  createShopReviewSuccess,
  editReviewSuccess,
  editShopReviewSuccess,
  deleteReviewSuccess,
  deleteShopReviewSuccess,
  updateReviewSummary,
  updateShopReviewData,
  CREATE_SHOP_REVIEW_SUCCESS,
  EDIT_SHOP_REVIEW_SUCCESS,
  DELETE_SHOP_REVIEW_SUCCESS,
  CREATE_REVIEW_SUCCESS,
  EDIT_REVIEW_SUCCESS,
  DELETE_REVIEW_SUCCESS,
  CLOSE_REVIEW_DIALOG,
} from '../actions';

import { setDisplayName } from '../../account/settings/actions/display-name';

// Remove all query params related to reviews when closing the form
function* removeReviewQueryParams() {
  const { push } = window.__history;
  const { review, review_id: reviewId, code, ...queryParams } = qs.parse(
    window.location.search
  );

  if (!review && !reviewId && !code) return;

  yield call(
    push,
    decodeURIComponent(
      `${window.location.pathname}?${qs.stringify(queryParams)}`
    )
  );
}

// Update review data on profile pages
function* updateProfileReviews(shouldSave) {
  const { limit } = yield select(state => state.areas.review.pageInfo.profile);
  const { isAuthenticated } = yield select(
    state => state.areas.account.isAuthenticated
  );

  if (!isAuthenticated) return;

  try {
    const response = yield call(
      graphql,
      `
        query updateProfileReviews(
          $limit: Int = 10
          $getProduct: Boolean = true
          $getStore: Boolean = true
        ) {
          me {
            reviews(offset: 0, limit: $limit) {
              totalItems
              pageInfo {
                limit
                offset
                hasNext
                hasPrevious
              }
              nodes {
                ...GenericUserReview
              }
            }
          }
        }
        ${genericUserReviewFragment}`,
      {
        limit,
      }
    );

    if (response.errors?.length) {
      yield put(
        reviewRequestError(
          response.errors[0].code ?? response.errors[0].extensions?.code
        )
      );
      yield call(captureException, response.errors);
      return;
    }

    if (shouldSave) {
      yield put(
        updateShopReviewData({
          nodes: response.data.me.reviews.nodes,
        })
      );
    }
  } catch (e) {
    yield call(captureException, e);
  }
}

// Updates review data and invalidates previous cached results
function* updateReviewsData({ shopId, productId, location }) {
  // Dont overwrite updated data with data from a different page.
  // If update came from a profile page, save the updated profile data.
  // Otherwise, save product or store data.
  const shouldSaveProfileData = Boolean(
    location && location.includes('/account/profile')
  );

  // Updates and invalidates cache for profile reviews
  yield call(updateProfileReviews, shouldSaveProfileData);

  if (!shopId && !productId) return;

  let type;
  if (shopId) type = 'store';
  if (productId) type = 'product';

  // Get the current pageInfo limit for the query in order to invalidate
  // the cache of the correct request
  const { limit } = yield select(state => state.areas.review.pageInfo[type]);

  try {
    const response = yield call(
      graphql,
      `query updateReviews($id: Int!, $limit: Int!, $getProduct: Boolean = false, $getStore: Boolean = false) {
          ${type}(id: $id) {
            userReviews(limit: $limit, offset: 0, invalidateCache: true) {
              nodes {
                ...GenericUserReview
              }
              myReview {
                id
                rating
                reviewText
                createdAt
                localizedCreatedAt: createdAt(format: "L")
                localizedUpdatedAt: updatedAt(format: "L")
              }
            }

          }
        }, ${genericUserReviewFragment}`,
      { id: shopId || productId, limit, getProduct: type === 'product' }
    );

    if (response.errors?.length) {
      yield put(
        reviewRequestError(
          response.errors[0].code ?? response.errors[0].extensions?.code
        )
      );
      yield call(captureException, response.errors);
      return;
    }

    if (!shouldSaveProfileData) {
      // Updates and invalidates cache for store and product pages
      yield put(
        updateShopReviewData({
          shopId,
          productId,
          nodes: response.data[type].userReviews.nodes,
          myReview: response.data[type].userReviews.myReview,
        })
      );
    }
  } catch (e) {
    yield call(captureException, e);
  }
}

function* updateSummaryData(id, type) {
  const response = yield graphql(
    `query updateReviewSummary($id: Int!) {
      ${type.toLowerCase()}(id: $id) {
        userReviewSummary {
          rating
          count
          countTotal
          distribution
        }
      }
    }
    `,
    { id }
  );

  if (response.errors?.length) {
    yield put(
      reviewRequestError(
        response.errors[0].code ?? response.errors[0].extensions?.code
      )
    );
    yield call(captureException, response.errors);
  }

  if (
    response.data &&
    response.data.product &&
    response.data.product.userReviewSummary
  ) {
    yield put(
      updateReviewSummary({
        ...response.data.product.userReviewSummary,
        belongsToId: id,
      })
    );
  }
}

export function* createReview({
  belongsToId,
  rating,
  reviewType: type,
  reviewText,
  purchaseDate,
  displayName,
}) {
  try {
    const response = yield call(
      graphql,
      `mutation reviewCreate(
        $belongsToId: Int!,
        $type: reviewType!,
        $rating: Int!,
        $reviewText: String!,
        $purchaseDate: String
        $displayName: String
        $getProduct: Boolean = false
        $getStore: Boolean = false) {
          reviewCreate(
            type: $type,
            belongsToId: $belongsToId,
            rating: $rating,
            reviewText: $reviewText,
            purchaseDate: $purchaseDate
            displayName: $displayName) {
              ...GenericUserReview
            }
      }, ${genericUserReviewFragment}`,
      {
        type,
        belongsToId,
        rating: parseInt(rating, 10),
        reviewText,
        purchaseDate,
        displayName,
      }
    );

    if (response.errors?.length) {
      yield put(
        reviewRequestError(
          response.errors[0].code ?? response.errors[0].extensions?.code
        )
      );
      yield call(captureException, response.errors);
      return;
    }

    if (displayName) {
      yield put(setDisplayName(displayName));
    }

    yield put(closeReviewDialog());
    yield put(
      createReviewSuccess({
        productId: belongsToId,
      })
    );

    yield call(updateSummaryData, belongsToId, type);

    yield put(
      promptToast(
        'success',
        window.APP_GLOBALS.t('reviews', 'dialog.create-success')
      )
    );
  } catch (e) {
    yield call(captureException, e);
  }
}

export function* createShopReview({
  belongsToId,
  belongsToName,
  rating,
  reviewType,
  reviewText,
  purchaseDate,
  answers,
  email,
  displayName,
}) {
  try {
    const response = yield call(
      graphql,
      `mutation reviewCreate(
        $belongsToId: Int!,
        $type: reviewType!,
        $rating: Int!,
        $reviewText: String!,
        $purchaseDate: String,
        $answers: [AnswerInput]
        $email: String
        $displayName: String
        $getProduct: Boolean = false
        $getStore: Boolean = false) {
          reviewCreate(
            type: $type,
            belongsToId: $belongsToId,
            rating: $rating,
            reviewText: $reviewText,
            purchaseDate: $purchaseDate,
            answers: $answers,
            email: $email
            displayName: $displayName) {
              ...GenericUserReview
            }
      }, ${genericUserReviewFragment}`,
      {
        type: reviewType,
        belongsToId,
        rating: parseInt(rating, 10),
        reviewText,
        purchaseDate,
        answers,
        email,
        displayName,
      }
    );

    if (response.errors?.length) {
      yield put(
        reviewRequestError(
          response.errors[0].code ?? response.errors[0].extensions?.code
        )
      );
      yield call(captureException, response.errors);
      return;
    }

    if (displayName) {
      yield put(setDisplayName(displayName));
    }

    yield call(updateSummaryData, belongsToId, reviewType);
    yield put(
      createShopReviewSuccess(
        // Don't update data if review needs to be verified before published
        !email ? { shopId: belongsToId } : {}
      )
    );
    yield put(closeReviewDialog());

    // tracking
    yield call(storeRated, {
      type: ShopReviewType.SHOP,
      rating,
      storeId: belongsToId,
      storeName: belongsToName,
    });
    yield call(storeReviewCreated, {
      type: ShopReviewType.SHOP,
      storeId: belongsToId,
      storeName: belongsToName,
    });

    if (response.data.reviewCreate.isPublished) {
      yield call(storeReviewPublished, {
        type: ShopReviewType.SHOP,
        storeId: belongsToId,
        storeName: belongsToName,
      });
    }
  } catch (e) {
    yield call(captureException, e);
  }
}

export function* editReview({
  reviewId,
  reviewType,
  rating,
  reviewText,
  purchaseDate,
  belongsToId,
  location,
}) {
  try {
    const response = yield call(
      graphql,
      `mutation reviewEdit(
        $reviewId: String!,
        $rating: Int!,
        $reviewText: String!,
        $purchaseDate: String,
        $getProduct: Boolean = false
        $getStore: Boolean = false
      ) {
        reviewEdit(
          reviewId: $reviewId,
          rating: $rating,
          reviewText: $reviewText,
          purchaseDate: $purchaseDate,
        ) {
          ...GenericUserReview
        }
      }, ${genericUserReviewFragment}`,
      {
        reviewId,
        rating: parseInt(rating, 10),
        reviewText,
        purchaseDate,
        getProduct: reviewType === 'PRODUCT',
      }
    );

    if (response.errors?.length) {
      yield put(
        reviewRequestError(
          response.errors[0].code ?? response.errors[0].extensions?.code
        )
      );
      yield call(captureException, response.errors);
      return;
    }

    yield put(closeReviewDialog());
    yield put(
      editReviewSuccess({
        productId: belongsToId,
        location,
      })
    );
    yield call(updateSummaryData, belongsToId, reviewType);

    yield put(
      promptToast(
        'success',
        window.APP_GLOBALS.t('reviews', 'dialog.edit-success')
      )
    );
  } catch (e) {
    yield call(captureException, e);
  }
}

export function* editShopReview({
  reviewId,
  reviewType,
  rating,
  reviewText,
  purchaseDate,
  belongsToId,
  belongsToName,
  answers,
  token,
  location,
}) {
  try {
    const response = yield call(
      graphql,
      `mutation editShopReview(
        $reviewId: ID!
        $rating: Int!
        $reviewText: String!
        $purchaseDate: String
        $answers: [AnswerInput]
        $token: String
      ) {
        editShopReview(
          reviewId: $reviewId,
          rating: $rating,
          reviewText: $reviewText,
          purchaseDate: $purchaseDate,
          answers: $answers
          token: $token
        ) {
          success
        }
      }`,
      {
        reviewId,
        rating: parseInt(rating, 10),
        reviewText,
        purchaseDate,
        answers,
        token,
      }
    );

    if (response.errors?.length) {
      yield put(
        reviewRequestError(
          response.errors[0].code ?? response.errors[0].extensions?.code
        )
      );
      yield call(captureException, response.errors);
      return;
    }

    yield put(
      editShopReviewSuccess({
        shopId: belongsToId,
        location,
      })
    );

    yield call(updateSummaryData, belongsToId, reviewType);

    yield put(closeReviewDialog());
    yield put(
      promptToast(
        'success',
        window.APP_GLOBALS.t('reviews', 'dialog.edit-success')
      )
    );

    // tracking
    yield call(storeReviewEdited, {
      type: ShopReviewType.SHOP,
      storeId: belongsToId,
      storeName: belongsToName,
    });
  } catch (e) {
    yield call(captureException, e);
  }
}

export function* deleteReview({ reviewId, reviewType, belongsToId, location }) {
  try {
    const response = yield call(
      graphql,
      `mutation reviewDelete(
        $reviewId: String!,
      ) {
        reviewDelete(
          reviewId: $reviewId,
        ) {
          success
        }
      }`,
      {
        reviewId,
      }
    );

    if (response.errors?.length) {
      yield put(
        reviewRequestError(
          response.errors[0].code ?? response.errors[0].extensions?.code
        )
      );
      yield call(captureException, response.errors);
      return;
    }

    yield put(closeReviewDialog());
    yield put(
      deleteReviewSuccess({
        productId: belongsToId,
        location,
      })
    );
    yield call(updateSummaryData, belongsToId, reviewType);

    yield put(
      promptToast(
        'success',
        window.APP_GLOBALS.t('reviews', 'dialog.delete-success')
      )
    );
  } catch (e) {
    yield call(captureException, e);
  }
}

export function* deleteShopReview({
  reviewId,
  token,
  shopId,
  shopName,
  location,
}) {
  try {
    const response = yield call(
      graphql,
      `
      mutation($reviewId: ID!, $token: String) {
        deleteShopReview(id: $reviewId, token: $token) {
          success
        }
      }
    `,
      { reviewId, token }
    );

    if (response.errors?.length) {
      yield put(
        reviewRequestError(
          response.errors[0].code ?? response.errors[0].extensions?.code
        )
      );
      yield call(captureException, response.errors);
      return;
    }

    yield put(deleteShopReviewSuccess({ shopId, reviewId, location }));
    yield put(closeReviewDialog());

    yield put(
      promptToast(
        'success',
        window.APP_GLOBALS.t('reviews', 'dialog.delete-success')
      )
    );

    // tracking
    yield call(storeReviewDeleted, {
      type: ShopReviewType.SHOP,
      storeId: shopId,
      storeName: shopName,
    });
  } catch (e) {
    yield call(captureException, e);
  }
}

export function* reportReview({ reviewId, message, email }) {
  if (!email || !isValidEmail(email)) {
    yield put(reportReviewFailed('error.missing.email'));
    return;
  }
  if (!message) {
    yield put(reportReviewFailed('error.missing.message'));
    return;
  }
  try {
    const response = yield call(
      graphql,
      `mutation reviewReport(
        $reviewId: String!,
        $message: String!,
        $email: String!,
      ) {
        reviewReport(
          reviewId: $reviewId,
          message: $message,
          email: $email,
        ) {
          success
        }
      }`,
      { reviewId, message, email }
    );
    if (
      response &&
      response.data &&
      response.data.reviewReport &&
      response.data.reviewReport.success
    ) {
      yield put(reportReviewSuccess());
      yield put(
        promptToast(
          'success',
          window.APP_GLOBALS.t('feedback', 'report-review.success')
        )
      );
    } else {
      yield put(reportReviewFailed(response ? response.errors : undefined));
    }
  } catch (err) {
    yield call(captureException, err);
    yield put(reportReviewFailed(err.message));
  }
}

function* likeReview({ id, reviewType }) {
  const query = `
    mutation($reviewId: String!) {
      reviewVoteHelpful(reviewId: $reviewId) {
        success
      }
    }
  `;

  const response = yield call(graphql, query, { reviewId: id });

  if (
    response.data.reviewVoteHelpful &&
    response.data.reviewVoteHelpful.success
  ) {
    const likedReviews = yield select(
      state => state.areas.account.me.likes.reviews
    );
    const value = likedReviews && likedReviews.includes(id) ? 'unlike' : 'like';

    if (reviewType === 'product') {
      yield call(productReviewVoted, value);
    }

    if (reviewType === 'store') {
      yield call(storeReviewVoted, value);
    }

    yield put(likeReviewSuccess(id, value));
  } else {
    yield put(likeReviewFailed());
  }
}

function* mySaga() {
  yield takeLatest(CREATE_REVIEW, createReview);
  yield takeLatest(CREATE_SHOP_REVIEW, createShopReview);
  yield takeLatest(EDIT_SHOP_REVIEW, editShopReview);
  yield takeLatest(EDIT_REVIEW, editReview);
  yield takeLatest(DELETE_REVIEW, deleteReview);
  yield takeLatest(DELETE_SHOP_REVIEW, deleteShopReview);
  yield takeLatest(REPORT_REVIEW, reportReview);
  yield takeLatest(LIKE_REVIEW_TOGGLE, likeReview);
  yield takeLatest(
    [
      CREATE_SHOP_REVIEW_SUCCESS,
      EDIT_SHOP_REVIEW_SUCCESS,
      DELETE_SHOP_REVIEW_SUCCESS,
      CREATE_REVIEW_SUCCESS,
      EDIT_REVIEW_SUCCESS,
      DELETE_REVIEW_SUCCESS,
    ],
    updateReviewsData
  );
  yield takeLatest(CLOSE_REVIEW_DIALOG, removeReviewQueryParams);
}

export default mySaga;
